/**
 * CallCentreConstants
 *
 * Mirror of `backend/Libraries/CCMSLib.Models/CallCentre/CallCentreConstants.cs`.
 * Seeded lookup IDs (271a_CallCentreManagement_seed_pg.sql). The lookup TABLES are the source of
 * truth for names/colours/icons (see `CallCentreLookupsView`); these constants only name the fixed
 * IDs the engine branches on. IDs start at 1, never 0. Keep additive.
 *
 * Each C# nested class is a `CALL_CENTRE_*` const object (Procedures style). `CallCentreConstants`
 * at the bottom groups them under the C# names (`CallCentreConstants.CampaignStatus.Active`).
 */
/** The service name API keys must list in AllowedServices. */
export const CALL_CENTRE_SERVICE_NAME = 'callcentre-management';
/** AppRegistry.AppName (pg/system/009). AppID is assigned by the database; look it up by name. */
export const CALL_CENTRE_APP_NAME = 'CallCentreManagement';
/** Source / audit tag other services record for writes the call centre makes (members, PCO links). */
export const CALL_CENTRE_SOURCE = 'call-centre';
export const CALL_CENTRE_CAMPAIGN_TYPE = {
    WeeklyFollowUp: 1,
    DiscoverActs: 2,
    CleanUp: 3,
    Referral: 4,
    Salvation: 5,
    Event: 6,
    Course: 7,
    Custom: 8,
};
export const CALL_CENTRE_CAMPAIGN_STATUS = {
    Draft: 1,
    Active: 2,
    Paused: 3,
    Completed: 4,
    Archived: 5,
};
export const CALL_CENTRE_CONTACT_STATUS = {
    Unassigned: 1,
    Assigned: 2,
    InProgress: 3,
    CallbackDue: 4,
    Done: 5,
    Unreachable: 6,
    DoNotCall: 7,
    Removed: 8,
};
/** Still to be worked (queues, workload and "open" counts). */
export const CALL_CENTRE_CONTACT_STATUS_OPEN = [
    CALL_CENTRE_CONTACT_STATUS.Unassigned,
    CALL_CENTRE_CONTACT_STATUS.Assigned,
    CALL_CENTRE_CONTACT_STATUS.InProgress,
    CALL_CENTRE_CONTACT_STATUS.CallbackDue,
];
/** Port of ContactStatus.IsOpen: Unassigned, Assigned, In progress or Callback due. */
export function callCentreContactStatusIsOpen(statusId) {
    return statusId != null && CALL_CENTRE_CONTACT_STATUS_OPEN.includes(statusId);
}
export const CALL_CENTRE_OUTCOME_GROUP = {
    Reached: 1,
    Declined: 2,
    Retry: 3,
    Closed: 4,
    Escalate: 5,
};
/** Seeded outcomes the engine recognises (admins can add more; behaviour comes from the outcome's flags). */
export const CALL_CENTRE_OUTCOME = {
    AnsweredComing: 1,
    AnsweredConnected: 2,
    AnsweredDeclined: 3,
    NoAnswer: 4,
    MessageLeft: 5,
    CallBackLater: 6,
    WrongNumber: 7,
    NeedsPastoralCare: 8,
    WantsToServe: 9,
    DoNotCall: 10,
};
export const CALL_CENTRE_HANDOFF_TYPE = {
    Care: 1,
    Ministry: 2,
    Other: 3,
};
export const CALL_CENTRE_HANDOFF_STATUS = {
    Open: 1,
    Accepted: 2,
    Done: 3,
    Declined: 4,
};
/** Port of HandoffStatus.IsOpen: Open or Accepted. */
export function callCentreHandoffStatusIsOpen(statusId) {
    return statusId === CALL_CENTRE_HANDOFF_STATUS.Open || statusId === CALL_CENTRE_HANDOFF_STATUS.Accepted;
}
export const CALL_CENTRE_LIST_SOURCE_TYPE = {
    Spreadsheet: 1,
    MemberFilter: 2,
    PlanningCentreList: 3,
    FormEntries: 4,
    ActsCareRegistrations: 5,
    Manual: 6,
    /** People who submitted a Planning Centre People form (pg 271l). */
    PlanningCentreForm: 7,
};
export const CALL_CENTRE_IMPORT_MATCH_STATUS = {
    Matched: 1,
    Possible: 2,
    New: 3,
    Duplicate: 4,
    Invalid: 5,
};
export const CALL_CENTRE_IMPORT_BATCH_STATUS = {
    Processing: 1,
    ReadyForReview: 2,
    Added: 3,
    Cancelled: 4,
};
export const CALL_CENTRE_PCO_LINK_STATUS = {
    Linked: 1,
    Found: 2,
    NotInPco: 3,
    NotChecked: 4,
};
export const CALL_CENTRE_SYNC_JOB_TYPE = {
    CreateMember: 1,
    LinkPcoPerson: 2,
    CreatePcoPerson: 3,
    PushDetailsToPco: 4,
    PushJourneyToPco: 5,
    ApplyDetailsToCcms: 6,
};
export const CALL_CENTRE_SYNC_STATUS = {
    Queued: 1,
    Running: 2,
    Done: 3,
    Failed: 4,
    /** Terminal: needs a person to decide (for example the PCO person is linked to another member). Never retried. */
    Conflict: 5,
    Cancelled: 6,
};
export const CALL_CENTRE_REMINDER_TYPE = {
    CallbackDue: 1,
    CloseYourCall: 2,
    UntouchedContacts: 3,
    CampaignEnding: 4,
    ConfirmAttendance: 5,
    HandoffWaiting: 6,
    ReviewWaiting: 7,
    NewAssignment: 8,
    /** A referral nobody picked up after EscalationDays: to supervisors. */
    HandoffEscalated: 9,
    /** AI care check (Stage 6) flagged possible danger in a caller's notes: to supervisors at once. */
    CareConcern: 10,
};
/** CallCentreNotificationTopic IDs: 1-9 are the reminder types, plus the daily summaries. */
export const CALL_CENTRE_NOTIFICATION_TOPIC = {
    CallerDigest: 20,
    AdminDigest: 21,
};
export const CALL_CENTRE_ASSIGNMENT_STRATEGY = {
    Manual: 1,
    Balanced: 2,
    SelfClaim: 3,
    MinistryOwner: 4,
    /** Callers at the contact's campus first, then everyone. */
    ByCampus: 5,
};
export const CALL_CENTRE_DETAIL_CHANGE_MODE = {
    /** Campaigns only: use CallCentreSetting.DetailChangeModeID. */
    Inherit: 1,
    ReviewFirst: 2,
    ApplyImmediately: 3,
};
export const CALL_CENTRE_DETAIL_CHANGE_STATUS = {
    Pending: 1,
    Applied: 2,
    Rejected: 3,
    Failed: 4,
    /** 271j: an "add when missing" change that found a value already there (nothing was overwritten). Terminal. */
    Skipped: 5,
};
/** 271m: CallCentreMessageStatus, a message to the person called (per channel). */
export const CALL_CENTRE_MESSAGE_STATUS = {
    Queued: 1,
    /** Held until the next calling hours, then sent. */
    Scheduled: 2,
    NotSent: -1,
    Failed: -2,
};
/**
 * 271j: CallCentreMemberFieldKind, what a member field reference points at. The code is the field key's prefix:
 * "builtin:FirstName" (a member property), "contact:1" (a contact list use: 1 Calling, 2 WhatsApp, 3 SMS, 4 Email),
 * "custom:allergies" (a member custom field key), "pco:123456" (271n: a Planning Centre field definition id).
 */
export const CALL_CENTRE_MEMBER_FIELD_KIND = {
    BuiltIn: 1,
    ContactUse: 2,
    CustomField: 3,
    /** 271n: a Planning Centre People custom field (only for members linked to Planning Centre). */
    PlanningCentre: 4,
};
/** 271j: the field key prefix of each member field kind. */
export const CALL_CENTRE_MEMBER_FIELD_KIND_CODE = {
    BuiltIn: 'builtin',
    ContactUse: 'contact',
    CustomField: 'custom',
    PlanningCentre: 'pco',
};
/** 271j: a member field key ("builtin:FirstName", "contact:1", "custom:allergies"). */
export function callCentreMemberFieldKey(kindId, fieldRef) {
    const code = kindId === CALL_CENTRE_MEMBER_FIELD_KIND.BuiltIn
        ? CALL_CENTRE_MEMBER_FIELD_KIND_CODE.BuiltIn
        : kindId === CALL_CENTRE_MEMBER_FIELD_KIND.ContactUse
            ? CALL_CENTRE_MEMBER_FIELD_KIND_CODE.ContactUse
            : kindId === CALL_CENTRE_MEMBER_FIELD_KIND.CustomField
                ? CALL_CENTRE_MEMBER_FIELD_KIND_CODE.CustomField
                : kindId === CALL_CENTRE_MEMBER_FIELD_KIND.PlanningCentre
                    ? CALL_CENTRE_MEMBER_FIELD_KIND_CODE.PlanningCentre
                    : String(kindId);
    return `${code}:${fieldRef}`;
}
/** 271j: the kind and reference of a member field key (null when it isn't one). The server checks it properly. */
export function parseCallCentreMemberFieldKey(key) {
    const m = /^\s*([a-z]+):(.+?)\s*$/i.exec(key ?? '');
    if (!m)
        return null;
    const code = m[1].toLowerCase();
    const kindID = code === CALL_CENTRE_MEMBER_FIELD_KIND_CODE.BuiltIn
        ? CALL_CENTRE_MEMBER_FIELD_KIND.BuiltIn
        : code === CALL_CENTRE_MEMBER_FIELD_KIND_CODE.ContactUse
            ? CALL_CENTRE_MEMBER_FIELD_KIND.ContactUse
            : code === CALL_CENTRE_MEMBER_FIELD_KIND_CODE.CustomField
                ? CALL_CENTRE_MEMBER_FIELD_KIND.CustomField
                : code === CALL_CENTRE_MEMBER_FIELD_KIND_CODE.PlanningCentre
                    ? CALL_CENTRE_MEMBER_FIELD_KIND.PlanningCentre
                    : null;
    return kindID == null ? null : { kindID, fieldRef: m[2] };
}
/** Member property names a caller may propose a change for (MemberManagement apply-fields keys). */
export const CALL_CENTRE_DETAIL_FIELDS = {
    FirstName: 'FirstName',
    LastName: 'LastName',
    Email: 'Email',
    CellNumber: 'CellNumber',
};
/** DetailFields.All */
export const CALL_CENTRE_DETAIL_FIELDS_ALL = [
    CALL_CENTRE_DETAIL_FIELDS.FirstName,
    CALL_CENTRE_DETAIL_FIELDS.LastName,
    CALL_CENTRE_DETAIL_FIELDS.Email,
    CALL_CENTRE_DETAIL_FIELDS.CellNumber,
];
/** Port of DetailFields.Label: the display label for a detail field (unknown fields come back unchanged). */
export function callCentreDetailFieldLabel(field) {
    switch (field) {
        case CALL_CENTRE_DETAIL_FIELDS.FirstName:
            return 'First name';
        case CALL_CENTRE_DETAIL_FIELDS.LastName:
            return 'Surname';
        case CALL_CENTRE_DETAIL_FIELDS.Email:
            return 'Email';
        case CALL_CENTRE_DETAIL_FIELDS.CellNumber:
            return 'Cell number';
        default:
            return field;
    }
}
export const CALL_CENTRE_ATTEMPT_STATUS = {
    LoggedManually: 1,
    Dialling: 2,
    Answered: 3,
    NoAnswer: 4,
    Busy: 5,
    Failed: 6,
};
export const CALL_CENTRE_QUESTION_TYPE = {
    ShortText: 1,
    Number: 2,
    YesNo: 3,
    Choice: 4,
};
/** CallCentreGuideStepKind (271i): what a conversation guide step does. */
export const CALL_CENTRE_GUIDE_STEP_KIND = {
    /** Something the caller says, then carries on (one option: the way on). */
    Say: 1,
    /** A question with an answer button per reply. */
    Ask: 2,
    /** The end of the conversation; may suggest an outcome the caller still confirms. */
    End: 3,
    /** 271k: the caller types or picks a value (one option: Next; optional rules branch on it). */
    Input: 4,
    /** 271k: goes on by itself: ordered rules (first match wins), and one option: Otherwise. */
    Check: 5,
};
/** CallCentreGuideValueType (271k): what an Input step takes and the kind of value a rule compares. */
export const CALL_CENTRE_GUIDE_VALUE_TYPE = {
    Text: 1,
    Number: 2,
    Date: 3,
    YesNo: 4,
    /** Facts with a fixed list (an earlier answer, campus, list source, campaign type). Never an Input step. */
    Choice: 5,
};
/** CallCentreGuideOperator (271k): how a rule compares. */
export const CALL_CENTRE_GUIDE_OPERATOR = {
    Is: 1,
    IsNot: 2,
    /** Numbers: more than. Dates: after. */
    MoreThan: 3,
    /** Numbers: less than. Dates: before. */
    LessThan: 4,
    Contains: 5,
    IsEmpty: 6,
    IsNotEmpty: 7,
    /** Journey steps only. */
    HasCompleted: 8,
    HasNotCompleted: 9,
};
/** CallCentreGuideFactSource (271k): what a rule looks at. */
export const CALL_CENTRE_GUIDE_FACT_SOURCE = {
    /** The answer picked at an Ask step earlier in the call (factRef: the step key; value: an answer key). */
    Answer: 1,
    /** The value typed at an Input step (factRef: the step key; that step's value type). */
    Typed: 2,
    /** Linked to a CCMS member (yes or no). */
    Member: 3,
    /** The contact's campus (value: a LocationID; 0 is Online Campus). */
    Campus: 4,
    /** A journey step completed or not (factRef: DA, E1 to E5). */
    Journey: 5,
    /** Calls to this person on this list before this one. */
    Tries: 6,
    /** The contact's list source type. */
    ListSource: 7,
    CampaignType: 8,
    /** A non-sensitive member custom field (factRef: the field key). */
    CustomField: 9,
};
export const CALL_CENTRE_JOURNEY_STEP_STATUS = {
    Booked: 1,
    Completed: 2,
    Cancelled: 3,
    NoShow: 4,
};
export const CALL_CENTRE_JOURNEY_BOOKING_TARGET_TYPE = {
    None: 1,
    CcmsEvent: 2,
    CcmsMeeting: 3,
    ActsCareCourse: 4,
    AnatomyMeeting: 5,
    PlanningCentre: 6,
};
export const CALL_CENTRE_JOURNEY_PCO_SYNC_MODE = {
    Off: 1,
    CustomField: 2,
    WorkflowCard: 3,
};
/** Where a journey entry came from (PersonJourneyStep.Source). */
export const CALL_CENTRE_JOURNEY_SOURCE = {
    CallCentre: 'call-centre',
    Admin: 'admin',
    ActsCare: 'acts-care',
    Anatomy: 'anatomy',
    PlanningCentre: 'planning-centre',
    Import: 'import',
};
/**
 * The caller queue's ordering groups (derived, never stored): callbacks that are due, then people not called yet,
 * then retries whose spacing has passed. Waiting = a retry or callback that is not due yet.
 */
export const CALL_CENTRE_QUEUE_GROUP = {
    CallbackDue: 1,
    New: 2,
    RetryReady: 3,
    Waiting: 4,
};
/** AspNetRights names (pg/identity/010). */
export const CALL_CENTRE_RIGHTS = {
    Read: 'CallCentre.Read',
    CampaignsManage: 'CallCentre.Campaigns.Manage',
    Import: 'CallCentre.Import',
    Assign: 'CallCentre.Assign',
    ContactsEdit: 'CallCentre.Contacts.Edit',
    ContactsExport: 'CallCentre.Contacts.Export',
    JourneyManage: 'CallCentre.Journey.Manage',
    HandoffsManage: 'CallCentre.Handoffs.Manage',
    SettingsManage: 'CallCentre.Settings.Manage',
    TelephonyManage: 'CallCentre.Telephony.Manage',
    IntegrationsManage: 'CallCentre.Integrations.Manage',
    CallsMake: 'CallCentre.Calls.Make',
    AiUse: 'CallCentre.AI.Use',
    /** The member profile's Journey card: people who can see members OR the call centre. Comma list as used by [CcmsAuthorize]. */
    JourneyBadgeRead: 'CallCentre.Read,Member.Read',
};
/** AspNetRoles names (pg/identity/010). */
export const CALL_CENTRE_ROLES = {
    Admin: 'Admin',
    CallCentreAdmin: 'CallCentreAdmin',
    CallCentreSupervisor: 'CallCentreSupervisor',
    CallCentreCaller: 'CallCentreCaller',
    /** Every role that may open the console module (rights narrow it further). Comma list as used by [CcmsAuthorize]. */
    ConsoleRoles: 'Admin,CallCentreAdmin,CallCentreSupervisor',
    /** Roles that may change settings, telephony and integrations (rights narrow it further). */
    FullAccessRoles: 'Admin,CallCentreAdmin',
    /** Every role that may use the caller portal. */
    PortalRoles: 'Admin,CallCentreAdmin,CallCentreSupervisor,CallCentreCaller',
};
/** Communication worker TemplateIDs (pg/communications/007, Stage 5). Block 65000-65029. */
export const CALL_CENTRE_TEMPLATES = {
    BlockStart: 65000,
    BlockEnd: 65029,
    /** A reminder (title, body, link): callbacks, close your call, referrals, reviews, assignments. */
    Reminder: 65000,
    /** Daily summary for a caller: today's list and callbacks. */
    CallerDigest: 65001,
    /** Daily summary for admins: progress, handoffs and reviews waiting. */
    AdminDigest: 65002,
    /** A referral nobody picked up: escalated to supervisors. */
    HandoffEscalation: 65003,
    /** 271m: a message to the person called, after a call (the WhatsApp template to link in Communications). */
    PersonMessage: 65010,
};
/** AIManagement AIFeatureFlag.FeatureType values (vendor 0, seeded off; Stage 6). */
export const CALL_CENTRE_AI_FEATURES = {
    CallAssist: 'CallCentreCallAssist',
    CareFlag: 'CallCentreCareFlag',
    ScriptAssist: 'CallCentreScriptAssist',
    ImportAssist: 'CallCentreImportAssist',
    Insights: 'CallCentreInsights',
    /** Build with AI for conversation guides (pg/aimanagement/009): Anthropic Claude on Amazon Bedrock only. */
    GuideBuilder: 'CallCentreGuideBuilder',
};
/** Error codes carried in ResponseData.errors[0] (the portal and console branch on these). */
export const CALL_CENTRE_ERROR_CODES = {
    /** Signed in, but not a caller yet: the portal shows "ask an admin for access". */
    NoAccess: 'no_access',
    /** The contact is not on this caller's list. */
    NotAssigned: 'not_assigned',
    CampaignNotActive: 'campaign_not_active',
    CampaignNotFound: 'campaign_not_found',
    ContactNotFound: 'contact_not_found',
    ContactClosed: 'contact_closed',
    OutsideCallWindow: 'outside_call_window',
    DoNotCall: 'do_not_call',
    NoteRequired: 'note_required',
    CallbackRequired: 'callback_required',
    ConsentRequired: 'consent_required',
    InvalidPhone: 'invalid_phone',
    DuplicateContact: 'duplicate_contact',
    ImportNotReady: 'import_not_ready',
    SelfClaimDisabled: 'self_claim_disabled',
    NothingToClaim: 'nothing_to_claim',
    DetailChangesDisabled: 'detail_changes_disabled',
    MemberServiceUnavailable: 'member_service_unavailable',
    StepInUse: 'step_in_use',
    OutcomeInUse: 'outcome_in_use',
    NothingToLink: 'nothing_to_link',
    PhoneSystemUnavailable: 'phone_system_unavailable',
    /** Stage 6: the AI helper is switched off (call centre AI switch or the AIManagement flag): hide the AI action. */
    AiUnavailable: 'ai_unavailable',
    /** Stage 6: the AI service failed or timed out: carry on without it. */
    AiFailed: 'ai_failed',
    /** Build with AI: Bedrock refused the Claude model (model access not enabled yet, or the flag names a non-Claude model). */
    AiModelUnavailable: 'ai_model_unavailable',
    /** Acts Care course dates or bookings could not be reached: book the date by hand for now (503). */
    CourseServiceUnavailable: 'course_service_unavailable',
    /** 271i: the conversation guide was saved by someone else since it was opened: reload before saving again. */
    GuideChanged: 'guide_changed',
    /** 271i: the conversation guide has problems (errors carry "steps.{key}.field: message"). */
    GuideInvalid: 'guide_invalid',
    /** 271i: the campaign type has no starter guide to copy. */
    NoStarterGuide: 'no_starter_guide',
    /** 271j: the member field is not one callers may change (or not in this way: add when missing / update). */
    MemberFieldNotAllowed: 'member_field_not_allowed',
    /** 271j: member field changes need the person linked to a CCMS member. */
    MemberNotLinked: 'member_not_linked',
};
/** Paths in the caller portal and console (reminder links, events). */
export const CALL_CENTRE_PATHS = {
    PortalCall: '/call',
    PortalCallbacks: '/callbacks',
    PortalReferrals: '/referrals',
    ConsoleBase: '/app/callcentre',
};
/** Hard limits that protect the API (requests above these are refused with 400). */
export const CALL_CENTRE_LIMITS = {
    ImportRowsPerRequest: 500,
    ImportRowsPerBatch: 20000,
    BulkContactIds: 5000,
    MaxPageSize: 200,
    SelfClaimMax: 50,
    QueueMax: 500,
    NoteMaxLength: 4000,
    /** 271i: conversation guides. */
    GuideMaxSteps: 200,
    GuideMaxOptionsPerStep: 12,
    GuideTitleMaxLength: 200,
    GuideBodyMaxLength: 4000,
    GuideLabelMaxLength: 200,
    GuideKeyMaxLength: 40,
    GuidePathMaxSteps: 300,
    /** 271j: one proposed member field value. */
    MemberFieldValueMaxLength: 500,
    /** 271j: values of one multi-value member field in one proposal. */
    MemberFieldMaxValues: 20,
    /** 271j: member field changes in one call. */
    MemberFieldChangesPerCall: 30,
    /** 271k: rules on one Check or Input step. */
    GuideMaxRulesPerStep: 12,
    /** 271k: a typed value or a rule's value. */
    GuideValueMaxLength: 500,
    GuidePlaceholderMaxLength: 200,
    GuideReasonMaxLength: 300,
    GuideFactRefMaxLength: 100,
    /** 271k: checks run one after another on the device before it stops (a loop of checks alone is refused on save). */
    GuideMaxAutoSteps: 50,
    /** 271m: messages to the people called. */
    MessageNameMaxLength: 100,
    MessageSubjectMaxLength: 200,
    MessageShortTextMaxLength: 1000,
    MessageEmailMaxLength: 5000,
    MessagesPerOutcome: 5,
};
/**
 * Every constant group under its C# name, for code ported from the backend:
 * `CallCentreConstants.ContactStatus.Open`, `CallCentreConstants.ErrorCodes.NoAccess`.
 */
export const CallCentreConstants = {
    ServiceName: CALL_CENTRE_SERVICE_NAME,
    AppName: CALL_CENTRE_APP_NAME,
    Source: CALL_CENTRE_SOURCE,
    CampaignType: CALL_CENTRE_CAMPAIGN_TYPE,
    CampaignStatus: CALL_CENTRE_CAMPAIGN_STATUS,
    ContactStatus: {
        ...CALL_CENTRE_CONTACT_STATUS,
        Open: CALL_CENTRE_CONTACT_STATUS_OPEN,
        isOpen: callCentreContactStatusIsOpen,
    },
    OutcomeGroup: CALL_CENTRE_OUTCOME_GROUP,
    Outcome: CALL_CENTRE_OUTCOME,
    HandoffType: CALL_CENTRE_HANDOFF_TYPE,
    HandoffStatus: {
        ...CALL_CENTRE_HANDOFF_STATUS,
        isOpen: callCentreHandoffStatusIsOpen,
    },
    ListSourceType: CALL_CENTRE_LIST_SOURCE_TYPE,
    ImportMatchStatus: CALL_CENTRE_IMPORT_MATCH_STATUS,
    ImportBatchStatus: CALL_CENTRE_IMPORT_BATCH_STATUS,
    PcoLinkStatus: CALL_CENTRE_PCO_LINK_STATUS,
    SyncJobType: CALL_CENTRE_SYNC_JOB_TYPE,
    SyncStatus: CALL_CENTRE_SYNC_STATUS,
    ReminderType: CALL_CENTRE_REMINDER_TYPE,
    NotificationTopic: CALL_CENTRE_NOTIFICATION_TOPIC,
    AssignmentStrategy: CALL_CENTRE_ASSIGNMENT_STRATEGY,
    DetailChangeMode: CALL_CENTRE_DETAIL_CHANGE_MODE,
    DetailChangeStatus: CALL_CENTRE_DETAIL_CHANGE_STATUS,
    MessageStatus: CALL_CENTRE_MESSAGE_STATUS,
    MemberFieldKind: {
        ...CALL_CENTRE_MEMBER_FIELD_KIND,
        Codes: CALL_CENTRE_MEMBER_FIELD_KIND_CODE,
        key: callCentreMemberFieldKey,
        parse: parseCallCentreMemberFieldKey,
    },
    DetailFields: {
        ...CALL_CENTRE_DETAIL_FIELDS,
        All: CALL_CENTRE_DETAIL_FIELDS_ALL,
        label: callCentreDetailFieldLabel,
    },
    AttemptStatus: CALL_CENTRE_ATTEMPT_STATUS,
    QuestionType: CALL_CENTRE_QUESTION_TYPE,
    JourneyStepStatus: CALL_CENTRE_JOURNEY_STEP_STATUS,
    JourneyBookingTargetType: CALL_CENTRE_JOURNEY_BOOKING_TARGET_TYPE,
    JourneyPcoSyncMode: CALL_CENTRE_JOURNEY_PCO_SYNC_MODE,
    JourneySource: CALL_CENTRE_JOURNEY_SOURCE,
    QueueGroup: CALL_CENTRE_QUEUE_GROUP,
    Rights: CALL_CENTRE_RIGHTS,
    Roles: CALL_CENTRE_ROLES,
    Templates: CALL_CENTRE_TEMPLATES,
    AiFeatures: CALL_CENTRE_AI_FEATURES,
    ErrorCodes: CALL_CENTRE_ERROR_CODES,
    Paths: CALL_CENTRE_PATHS,
    Limits: CALL_CENTRE_LIMITS,
};
//# sourceMappingURL=CallCentreConstants.js.map